import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getToken, setActiveApiLocale } from '../api';
import { useMe, useUpdateLocale } from '../data/queries';
import { DICTIONARIES, loadDictionary } from './dictionaries';
import {
  DEFAULT_LOCALE,
  detectBrowserLocale,
  isRtl,
  isSupported,
} from './locales';

const STORAGE_KEY = 'babysteps.locale';

export type TFn = (key: string, enDefault?: string) => string;

interface I18nContextValue {
  locale: string;
  dir: 'ltr' | 'rtl';
  setLocale: (code: string) => void;
  t: TFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** Initial locale before any account loads: stored choice → browser → English. */
function initialLocale(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    /* ignore */
  }
  return detectBrowserLocale();
}

// Make the very first API calls carry the resolved locale (module load, before render).
const BOOT_LOCALE = initialLocale();
setActiveApiLocale(BOOT_LOCALE);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(BOOT_LOCALE);
  const [loadedLocales, setLoadedLocales] = useState<Record<string, boolean>>({ en: true });

  // Lazily load translation dictionaries on-demand
  useEffect(() => {
    if (locale === 'en' || DICTIONARIES[locale]) return;
    let alive = true;
    loadDictionary(locale).then((dict) => {
      if (alive && dict) {
        setLoadedLocales((prev) => ({ ...prev, [locale]: true }));
      }
    });
    return () => { alive = false; };
  }, [locale]);

  // Only fetch /me when authenticated, to adopt the server-stored locale.
  const hasToken = getToken() !== null;
  const me = useMe(hasToken);
  const updateLocale = useUpdateLocale();

  // Apply locale side-effects: API header, <html lang/dir>, localStorage.
  useEffect(() => {
    setActiveApiLocale(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
    }
  }, [locale]);

  // Adopt the server-stored locale once /me loads (cross-device consistency).
  const serverLocale = me.data?.user?.locale;
  useEffect(() => {
    if (isSupported(serverLocale) && serverLocale !== locale) {
      setLocaleState(serverLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLocale]);

  const setLocale = useCallback(
    (code: string) => {
      const next = isSupported(code) ? code : DEFAULT_LOCALE;
      setLocaleState(next);
      setActiveApiLocale(next);
      if (getToken() !== null) {
        updateLocale.mutate(next); // persist to the user when signed in
      }
    },
    [updateLocale],
  );

  const t = useCallback<TFn>(
    (key, enDefault) => {
      const dict = DICTIONARIES[locale];
      return (
        dict?.[key] ??
        DICTIONARIES.en[key] ??
        enDefault ??
        key
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, loadedLocales],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir: isRtl(locale) ? 'rtl' : 'ltr', setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

const DEFAULT_CONTEXT_VALUE: I18nContextValue = {
  locale: BOOT_LOCALE,
  dir: isRtl(BOOT_LOCALE) ? 'rtl' : 'ltr',
  setLocale: () => {},
  t: (key: string, enDefault?: string) => DICTIONARIES.en[key] ?? enDefault ?? key,
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    if (import.meta.env.DEV) {
      console.warn('useI18n called outside <I18nProvider>; using default fallback');
    }
    return DEFAULT_CONTEXT_VALUE;
  }
  return ctx;
}

/** Convenience hook for just the translate function. */
export function useT(): TFn {
  return useI18n().t;
}
